import { Link, NavLink, useNavigate } from "react-router-dom";
import { List, X, MapPin, Sparkle, PaperPlaneTilt, ChartLineUp, SignOut, Globe, Compass, SignIn, User as UserIcon, Suitcase, NotePencil } from "@phosphor-icons/react";
import { useEffect, useRef, useState } from "react";
import { FlycherLogo } from "./FlycherLogo";
import { getAdminToken, clearAdminToken } from "@/lib/admin";
import { useAuth, buildLoginUrl } from "@/lib/AuthContext";

const links = [
  { to: "/", label: "Home", end: true },
  { to: "/explore", label: "Compare Prices" },
  { to: "/activities", label: "Activities" },
  { to: "/budget", label: "Budget" },
  { to: "/forex", label: "Forex" },
  { to: "/phrasebook", label: "Phrasebook" },
  { to: "/visa", label: "Visa" },
  { to: "/reality-check", label: "Reality Check" },
  { to: "/creator", label: "Creator" },
  { to: "/experts", label: "AI Experts" },
  { to: "/itinerary", label: "Itinerary" },
  { to: "/blog", label: "Blog" },
];

const quickDestinations = [
  { to: "/explore/vietnam", label: "Vietnam", emoji: "🇻🇳" },
  { to: "/explore/thailand", label: "Thailand", emoji: "🇹🇭" },
  { to: "/explore/bali", label: "Bali", emoji: "🇮🇩" },
  { to: "/explore/japan", label: "Japan", emoji: "🇯🇵" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  // Re-check admin status whenever the menu is toggled or on mount
  useEffect(() => {
    const check = () => setIsAdmin(Boolean(getAdminToken()));
    check();
    window.addEventListener("storage", check);
    return () => window.removeEventListener("storage", check);
  }, [open]);

  // Allow other components (e.g. mobile bottom tab bar) to open the drawer
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("flycher:open-menu", handler);
    return () => window.removeEventListener("flycher:open-menu", handler);
  }, []);

  // Lock body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Close profile dropdown on outside click
  useEffect(() => {
    if (!profileOpen) return;
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [profileOpen]);

  const closeMenu = () => setOpen(false);

  const handleLogout = () => {
    clearAdminToken();
    setIsAdmin(false);
    closeMenu();
    navigate("/");
  };

  const handleUserSignOut = async () => {
    await signOut();
    clearAdminToken();
    setIsAdmin(false);
    setProfileOpen(false);
    closeMenu();
    navigate("/");
  };

  // Login button uses dynamic window.location.origin — DO NOT hardcode.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginHref = typeof window !== "undefined" ? buildLoginUrl("/me/trips") : "#";

  return (
    <>
      <header
        data-testid="navbar"
        className="fixed top-0 left-0 right-0 z-50 glass"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="brand-logo"
            className="flex items-center group"
            onClick={closeMenu}
          >
            <FlycherLogo size={36} theme="dark" />
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm rounded-full transition-colors ${
                    isActive
                      ? "text-[#2A7FB8] bg-[#EAF4FB]"
                      : "text-slate-600 hover:text-slate-900"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <span className="hidden sm:inline font-mono text-[11px] text-slate-500">
              INR ₹
            </span>
            <Link
              to="/itinerary"
              data-testid="cta-plan-trip"
              className="hidden md:inline-flex px-4 py-2 rounded-full text-sm font-semibold shadow-sm hover:opacity-90 transition-opacity"
              style={{ background: "#FF7E4D", color: "#ffffff" }}
            >
              Plan a trip
            </Link>

            {/* Admin quick-access — shown whenever a super-admin JWT token
                is present in localStorage, regardless of Google-auth state.
                Otherwise a JWT-only admin has no way to reach the AutoBlog
                engine from the customer-facing site. */}
            {isAdmin && (
              <div className="relative hidden md:block">
                <button
                  type="button"
                  data-testid="nav-admin-menu-btn"
                  onClick={() => setProfileOpen((v) => !v)}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold border border-orange-400 text-orange-600 bg-orange-50 hover:bg-orange-100 transition-colors"
                >
                  <Sparkle size={12} weight="bold" />
                  Admin
                </button>
                {profileOpen && !user && (
                  <div
                    data-testid="nav-admin-menu-dropdown"
                    className="absolute right-0 mt-2 w-60 rounded-2xl bg-white shadow-xl border border-slate-100 overflow-hidden z-50"
                  >
                    <div className="px-4 py-3 border-b border-slate-100">
                      <div className="text-xs uppercase tracking-wider text-slate-500">
                        Super Admin
                      </div>
                    </div>
                    <Link
                      to="/admin"
                      data-testid="nav-admin-analytics-jwt"
                      onClick={() => setProfileOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50"
                    >
                      <ChartLineUp size={18} weight="duotone" color="#2A7FB8" />
                      Analytics Dashboard
                    </Link>
                    <Link
                      to="/admin/autoblog"
                      data-testid="nav-admin-autoblog-jwt"
                      onClick={() => setProfileOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 border-t border-slate-100"
                    >
                      <Sparkle size={18} weight="duotone" color="#F97316" />
                      AutoBlog Engine
                    </Link>
                    <button
                      type="button"
                      data-testid="nav-admin-signout-jwt"
                      onClick={() => {
                        clearAdminToken();
                        setIsAdmin(false);
                        setProfileOpen(false);
                        navigate("/");
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 text-left border-t border-slate-100"
                    >
                      <SignOut size={18} weight="duotone" />
                      Admin sign out
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Auth: Sign in OR avatar dropdown */}
            {user ? (
              <div className="relative" ref={profileRef}>
                <button
                  type="button"
                  data-testid="nav-avatar"
                  onClick={() => setProfileOpen((v) => !v)}
                  className="w-10 h-10 rounded-full overflow-hidden border border-slate-200 bg-white hover:border-[#2A7FB8] transition-colors grid place-items-center"
                  aria-label="Open profile menu"
                >
                  {user.picture ? (
                    <img
                      src={user.picture}
                      alt={user.name}
                      className="w-full h-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <UserIcon size={18} weight="duotone" color="#2A7FB8" />
                  )}
                </button>
                {profileOpen && (
                  <div
                    data-testid="nav-profile-dropdown"
                    className="absolute right-0 mt-2 w-64 rounded-2xl bg-white shadow-xl border border-slate-100 overflow-hidden"
                  >
                    <div className="px-4 py-3 border-b border-slate-100">
                      <div className="text-sm font-semibold text-slate-900 truncate">
                        {user.name}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {user.email}
                      </div>
                    </div>
                    <Link
                      to="/me/trips"
                      data-testid="nav-mytrips"
                      onClick={() => setProfileOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50"
                    >
                      <Suitcase size={18} weight="duotone" color="#2A7FB8" />
                      My Trips
                    </Link>
                    <Link
                      to="/me/notes"
                      data-testid="nav-mynotes"
                      onClick={() => setProfileOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 border-t border-slate-100"
                    >
                      <NotePencil size={18} weight="duotone" color="#FF7E4D" />
                      My Travel Notes
                    </Link>
                    {user.is_admin && (
                      <>
                        <Link
                          to="/admin"
                          data-testid="nav-admin-analytics"
                          onClick={() => setProfileOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 border-t border-slate-100"
                        >
                          <ChartLineUp size={18} weight="duotone" color="#2A7FB8" />
                          Analytics Dashboard
                        </Link>
                        <Link
                          to="/admin/autoblog"
                          data-testid="nav-admin-autoblog"
                          onClick={() => setProfileOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 border-t border-slate-100"
                        >
                          <Sparkle size={18} weight="duotone" color="#F97316" />
                          AutoBlog Engine
                        </Link>
                      </>
                    )}
                    <button
                      type="button"
                      data-testid="nav-signout"
                      onClick={handleUserSignOut}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 text-left border-t border-slate-100"
                    >
                      <SignOut size={18} weight="duotone" />
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <a
                href={loginHref}
                data-testid="nav-signin"
                className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-semibold border border-slate-200 bg-white text-slate-700 hover:border-[#2A7FB8] hover:text-[#2A7FB8] transition-colors"
              >
                <SignIn size={14} weight="bold" /> Sign in
              </a>
            )}

            <button
              data-testid="hamburger-toggle"
              aria-label={open ? "Close menu" : "Open menu"}
              onClick={() => setOpen((v) => !v)}
              className="w-10 h-10 rounded-full grid place-items-center border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition-colors"
            >
              {open ? <X size={18} weight="bold" /> : <List size={18} weight="bold" />}
            </button>
          </div>
        </div>
      </header>

      {/* Slide-over menu (works on all screen sizes) */}
      {open && (
        <>
          <div
            data-testid="menu-backdrop"
            onClick={closeMenu}
            className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity"
            style={{ zIndex: 10050 }}
          />
          <aside
            data-testid="hamburger-menu"
            className="fixed top-0 right-0 bottom-0 w-full sm:w-[380px] bg-white shadow-2xl overflow-y-auto"
            style={{ zIndex: 10100 }}
          >
            <div className="h-16 flex items-center justify-between px-5 border-b border-slate-100">
              <span className="text-sm font-semibold tracking-wide text-slate-500 uppercase">
                Menu
              </span>
              <button
                data-testid="menu-close"
                onClick={closeMenu}
                className="w-9 h-9 rounded-full grid place-items-center hover:bg-slate-100 text-slate-700"
                aria-label="Close menu"
              >
                <X size={18} weight="bold" />
              </button>
            </div>

            <div className="px-5 py-6 space-y-7">
              {/* Primary nav */}
              <section>
                <p className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-2">
                  Navigate
                </p>
                <div className="flex flex-col">
                  {links.map((l) => (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      end={l.end}
                      onClick={closeMenu}
                      data-testid={`menu-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 py-3 px-2 -mx-2 rounded-lg text-base transition-colors ${
                          isActive
                            ? "text-[#2A7FB8] bg-[#EAF4FB] font-semibold"
                            : "text-slate-700 hover:bg-slate-50"
                        }`
                      }
                    >
                      <Compass size={20} weight="duotone" />
                      {l.label}
                    </NavLink>
                  ))}
                </div>
              </section>

              {/* Quick destinations */}
              <section>
                <p className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-2">
                  Popular Destinations
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {quickDestinations.map((d) => (
                    <Link
                      key={d.to}
                      to={d.to}
                      onClick={closeMenu}
                      data-testid={`menu-destination-${d.label.toLowerCase()}`}
                      className="flex items-center gap-2 py-2.5 px-3 rounded-lg border border-slate-200 hover:border-[#2A7FB8] hover:bg-[#EAF4FB] text-sm text-slate-700 transition-colors"
                    >
                      <span className="text-lg">{d.emoji}</span>
                      <span className="font-medium">{d.label}</span>
                    </Link>
                  ))}
                </div>
              </section>

              {/* Actions */}
              <section>
                <p className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-2">
                  Plan & Share
                </p>
                <div className="flex flex-col gap-2">
                  <Link
                    to="/itinerary"
                    onClick={closeMenu}
                    data-testid="menu-plan-trip"
                    className="flex items-center justify-between gap-3 py-3 px-4 rounded-xl font-semibold shadow-sm hover:opacity-90 transition-opacity"
                    style={{ background: "#FF7E4D", color: "#ffffff" }}
                  >
                    <span className="flex items-center gap-2">
                      <Sparkle size={18} weight="fill" />
                      Plan a trip with AI
                    </span>
                    <PaperPlaneTilt size={18} weight="bold" />
                  </Link>
                  <Link
                    to="/experts"
                    onClick={closeMenu}
                    data-testid="menu-experts"
                    className="flex items-center gap-2 py-3 px-4 rounded-xl border border-slate-200 hover:bg-slate-50 text-sm font-medium text-slate-700"
                  >
                    <Globe size={18} weight="duotone" />
                    Chat with country experts
                  </Link>
                  <Link
                    to="/explore"
                    onClick={closeMenu}
                    data-testid="menu-explore"
                    className="flex items-center gap-2 py-3 px-4 rounded-xl border border-slate-200 hover:bg-slate-50 text-sm font-medium text-slate-700"
                  >
                    <MapPin size={18} weight="duotone" />
                    Browse destinations
                  </Link>
                </div>
              </section>

              {/* User account (Google sign-in) section */}
              <section data-testid="menu-account-section">
                <p className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-2">
                  Account
                </p>
                {user ? (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-3 py-3 px-2 -mx-2">
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-100 grid place-items-center">
                        {user.picture ? (
                          <img src={user.picture} alt={user.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                        ) : (
                          <UserIcon size={18} weight="duotone" color="#2A7FB8" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-900 truncate">{user.name}</div>
                        <div className="text-xs text-slate-500 truncate">{user.email}</div>
                      </div>
                    </div>
                    <Link
                      to="/me/trips"
                      onClick={closeMenu}
                      data-testid="menu-my-trips"
                      className="flex items-center gap-3 py-3 px-4 rounded-xl bg-[#EAF4FB] hover:bg-[#d8ecf7] text-[#2A7FB8] font-semibold transition-colors"
                    >
                      <Suitcase size={18} weight="duotone" />
                      My Trips
                    </Link>
                    <Link
                      to="/me/notes"
                      onClick={closeMenu}
                      data-testid="menu-my-notes"
                      className="flex items-center gap-3 py-3 px-4 rounded-xl bg-[#FFF1E8] hover:bg-[#fde3d3] text-[#FF7E4D] font-semibold transition-colors"
                    >
                      <NotePencil size={18} weight="duotone" />
                      My Travel Notes
                    </Link>
                    <button
                      onClick={handleUserSignOut}
                      data-testid="menu-user-signout"
                      type="button"
                      className="flex items-center gap-3 py-3 px-4 rounded-xl border border-slate-200 hover:bg-slate-50 text-sm font-medium text-slate-700 text-left"
                    >
                      <SignOut size={18} weight="duotone" />
                      Sign out
                    </button>
                  </div>
                ) : (
                  <a
                    href={loginHref}
                    data-testid="menu-signin"
                    onClick={closeMenu}
                    className="flex items-center justify-between gap-3 py-3 px-4 rounded-xl font-semibold shadow-sm hover:opacity-90 transition-opacity"
                    style={{ background: "#2A7FB8", color: "#ffffff" }}
                  >
                    <span className="flex items-center gap-2">
                      <SignIn size={18} weight="bold" />
                      Continue with Google
                    </span>
                    <PaperPlaneTilt size={18} weight="bold" />
                  </a>
                )}
              </section>

              {/* Admin section — visible to any super admin (JWT login OR Google sign-in with admin email) */}
              {(isAdmin || user?.is_admin) && (
                <section data-testid="admin-section">
                  <p className="text-[11px] font-mono uppercase tracking-wider text-[#2A7FB8] mb-2">
                    Super Admin
                  </p>
                  <div className="flex flex-col gap-2">
                    <Link
                      to="/admin"
                      onClick={closeMenu}
                      data-testid="menu-admin-analytics"
                      className="flex items-center gap-3 py-3 px-4 rounded-xl bg-[#EAF4FB] hover:bg-[#d8ecf7] text-[#2A7FB8] font-semibold transition-colors"
                    >
                      <ChartLineUp size={20} weight="duotone" />
                      Analytics Dashboard
                    </Link>
                    <Link
                      to="/admin/autoblog"
                      onClick={closeMenu}
                      data-testid="menu-admin-autoblog"
                      className="flex items-center gap-3 py-3 px-4 rounded-xl bg-orange-50 hover:bg-orange-100 text-orange-600 font-semibold transition-colors"
                    >
                      <Sparkle size={20} weight="duotone" />
                      AutoBlog Engine
                    </Link>
                    <button
                      onClick={handleLogout}
                      data-testid="menu-admin-logout"
                      className="flex items-center gap-3 py-3 px-4 rounded-xl border border-slate-200 hover:bg-slate-50 text-sm font-medium text-slate-700 text-left"
                    >
                      <SignOut size={18} weight="duotone" />
                      Sign out
                    </button>
                  </div>
                </section>
              )}

              <div className="pt-4 border-t border-slate-100 text-[11px] font-mono uppercase tracking-wider text-slate-400 text-center">
                Made for India · INR ₹
              </div>
            </div>
          </aside>
        </>
      )}
    </>
  );
};
